import { gql } from '@apollo/client';

/**
 * Lightweight queries that back the new detail-route Overview tabs. Each
 * picks the minimum fields the tab actually renders so the cache stays
 * cheap — the heavy popups (StudentPaymentPopup, EditGroupPopup,
 * TeacherEditPopup) keep using their own queries.
 */

export const StudentDetailQuery = gql`
  query StudentDetail($id: ID!) {
    student(id: $id) {
      id
      registrationDate
      acquisitionSource
      user {
        id
        firstName
        lastName
        middleName
        email
        phone
      }
      timeline {
        edges {
          node {
            id
            type
            date
            meta
          }
        }
      }
      accountsPayables {
        id
        amountForeign
        amountByn
        currency
        repaid
        startedAgreement {
          id
          agreementTitle
        }
      }
      agreements {
        id
        agreementTitle
        currency
        budgetAmount
        paidAmount
        terminated
        terminatedDate
        transferOut
        freeOfCharge
        statePrefinanced
        date
        partialAttendance
        paymentByCompany
        vat
        resident
        promocode
        comment
        legalName
        taxId
        street
        city
        zipCode
        address
        country {
          id
          title
        }
        company {
          id
          name
        }
        group {
          id
          name
          startDate
          course {
            id
            name
          }
        }
        firstLesson {
          id
          name
          lessonNumber
        }
        lastLesson {
          id
          name
          date
          lessonNumber
        }
        pandadocDocument {
          id
          pandadocId
          name
          status
          outcome
          docType
          course
          dateCompleted
          ttsHours
        }
        payments {
          id
          date
          amountAgreement
          currency
          source
          fakturowniaInvoice {
            id
            pk
            number
            status
            priceGross
            outstanding
            kind
            currency
          }
          contribution {
            id
          }
        }
        contributions(orderByDateFilter: true) {
          edges {
            node {
              id
              date
              amountForeign
              amountByn
              complete
              remainder
              delay
              status
              coveredAmount
              debt
              paidManually
              invoices {
                id
                pk
                number
                kind
                status
                paid
                netGross
                netPaid
                paymentTo
                currency
                provisionBlockedReason
                contributionMatchSource
                payment {
                  id
                  date
                  amountAgreement
                  currency
                }
              }
              payment {
                id
                date
                amountAgreement
                currency
                fakturowniaInvoice {
                  id
                  pk
                  number
                  status
                  priceGross
                  outstanding
                  kind
                  currency
                }
              }
            }
          }
        }
        chargeback {
          id
          currency
          amountForeign
          amountByn
          date
        }
        startAccountsPayable {
          id
          amountForeign
          currency
          repaid
        }
        corrections {
          id
          date
          amountForeign
          amountByn
          currency
          reason
          payment {
            id
          }
        }
      }
    }
  }
`;

export const GroupDetailQuery = gql`
  query GroupDetail($id: ID!) {
    group(groupId: $id) {
      id
      name
      startDate
      price
      currency
      studentsCount
      lessonsCount
      passedLessonsCount
      freeLessonsCount
      contributionsSum
      maxStudentsCount
      freePlaces
      statusFinished
      datetimeNextLesson
      deleteAvailable
      isFree
      comment
      publishedLms
      publishedLmsForTeacher
      publishedLmsForMethodist
      course {
        id
        name
      }
      teachers {
        id
        fullName
      }
    }
  }
`;

export const TeacherDetailQuery = gql`
  query TeacherDetail($id: ID!) {
    teacher(teacherId: $id) {
      id
      fullName
      accessToLms
      isDismissed
      groupsCount
      rating
      studentRating
      studentRatingCount
      studentComments {
        studentName
        comment
        createdAt
        courseName
        groupName
      }
      internalNote
      interviewRecording
      photo
      description
      user {
        id
        firstName
        lastName
        middleName
        email
        phone
      }
      courses
      coursesCanTeach {
        id
        name
      }
      employmentTypes {
        __typename
      }
      applications {
        edges {
          node {
            id
            title
            lessonsCount
            appointedLessonsCount
            servicePrice
            riaPrice
            currency
            isTerminated
            isCompleted
            teacher {
              id
              fullName
            }
            group {
              id
              name
            }
            employmentType {
              __typename
            }
          }
        }
      }
    }
  }
`;
